import React, { useState, useRef, useEffect, useCallback } from 'react';
import CustomToolTip from '../CustomToolTip';
import { showToastMessage } from '../../../utils/commonFunctions';
import './InlineInput.scss';

interface InlineInputProps {
  placeholder?: string;
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  disabled?: boolean;
  maxLength?: number;
  showTooltip?: boolean;
  tooltipTitle?: string;
  tooltipPlacement?: 'top' | 'top-start' | 'top-end' | 'bottom' | 'bottom-start' | 'bottom-end' | 'left' | 'left-start' | 'left-end' | 'right' | 'right-start' | 'right-end';
  triggerRecalc?: boolean;
  className?: string;
  'data-testid'?: string;
}

const InlineInput: React.FC<InlineInputProps> = ({
  placeholder = 'Enter Title',
  value = '',
  onChange,
  onBlur,
  onFocus,
  disabled = false,
  maxLength,
  showTooltip = true,
  tooltipTitle = '',
  tooltipPlacement = 'bottom',
  triggerRecalc = false,
  className = '',
  'data-testid': dataTestId,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [width, setWidth] = useState<string>('235px');
  const inputRef = useRef<HTMLInputElement>(null);
  const mirrorRef = useRef<HTMLDivElement>(null);

  // Calculate width based on content
  const calculateWidth = useCallback(() => {
    if (mirrorRef.current && inputRef.current) {
      // Update mirror with current value or placeholder
      mirrorRef.current.textContent = value || placeholder;

      // Force a reflow to get accurate measurements
      // Add 24px for padding (0 12px on both sides)
      const contentWidth = mirrorRef.current.offsetWidth;

      // If container is hidden (display:none), offsetWidth is 0 - do not calculate invalid width
      if (contentWidth === 0 && (value || placeholder)) {
        return;
      }

      if (isFocused) {
        // When focused (selected state), always show minimum 235px, expand to fit content up to 435px
        const newWidth = Math.min(Math.max(contentWidth, 235), 435);
        setWidth(newWidth + 'px');
      } else if (value) {
        // When not focused but has content, fit to content width (minimum 27px for single char, max 435px)
        const newWidth = Math.min(Math.max(contentWidth, 27), 435);
        setWidth(newWidth + 'px');
      } else {
        // When empty and not focused, show 235px
        setWidth('235px');
      }

      // Check if text is overflowing
      const isOverflow = inputRef.current.scrollWidth > inputRef.current.clientWidth;
      setIsOverflowing(isOverflow);
    }
  }, [value, placeholder, isFocused]);

  useEffect(() => {
    calculateWidth();
  }, [calculateWidth, triggerRecalc]);

  useEffect(() => {
    if (!inputRef.current) return;
    if (typeof IntersectionObserver !== 'undefined') {
      const observer = new IntersectionObserver((entries) => {
        if (entries[0]?.isIntersecting) {
          calculateWidth();
        }
      });
      observer.observe(inputRef.current);
      return () => observer.disconnect();
    }
  }, [calculateWidth]);

  const handleFocus = () => {
    setIsFocused(true);
    onFocus?.();
  };

  const handleBlur = () => {
    setIsFocused(false);
    onBlur?.();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    // Check for multiple consecutive spaces
    if (/  /.test(inputValue)) {
      showToastMessage('error', 'Multiple spaces are not allowed');
      // Remove extra spaces - keep only single spaces
      const cleanedValue = inputValue.replace(/  +/g, ' ');
      onChange?.(cleanedValue);
    } else {
      onChange?.(inputValue);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      (e.target as HTMLInputElement).blur();
    }
  };

  // Use overflow tooltip if text is being truncated, otherwise use provided tooltip
  const finalTooltipTitle = isOverflowing && !isFocused ? value : tooltipTitle;

  const input = (
    <>
      {/* Mirror element for measuring text width */}
      <div
        ref={mirrorRef}
        className="inline-input__mirror"
        aria-hidden="true"
      />

      <div
        className={`inline-input__wrapper ${isFocused ? 'selected' : ''} ${className}`.trim()}
        style={{ width: width }}
      >
        <input
          ref={inputRef}
          type="text"
          className={`inline-input__field ${isFocused ? 'focused' : ''} ${value ? 'has-value' : ''} ${isOverflowing && width === '435px' ? 'truncated' : ''}`}
          placeholder={placeholder}
          value={value}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          maxLength={maxLength}
          data-testid={dataTestId}
        />
      </div>
    </>
  );

  if (showTooltip) {
    return (
      <CustomToolTip
        title={finalTooltipTitle || ''}
        disabled={!finalTooltipTitle || disabled}
        placement={tooltipPlacement}
        className="inline-input__tooltip-wrapper"
      >
        {input}
      </CustomToolTip>
    );
  }

  return input;
};

export default InlineInput;
